import { useEffect, useMemo, useRef } from 'react';

import { Flex, Segmented, Table } from 'antd';
import type { SegmentedLabeledOption } from 'antd/es/segmented';

import { useScrollIntoView } from '@/shared/hooks';
import { useLocationHistoryStore } from '@/shared/stores';
import { TableEmpty } from '@/shared/ui/TableEmpty';

import type { ColumnQualityTableData } from '@/features/quality/lib/transformColumnQualityData';
import type { TableQualityTableData } from '@/features/quality/lib/transformTableQualityData';
import {
  assetQualityFilterStoreActions,
  useAssetQualityTable,
  useAssetQualityTabNavigation,
} from '@/features/quality/model';
import type { AssetQualityTabValue } from '@/features/quality/model';
import { AssetQualityFilter } from '@/features/quality/ui/dashboard/AssetQualityFilter';
import { CardHeader } from '@/features/quality/ui/dashboard/CardHeader';

import styles from './DataAssetQuality.module.css';

const ASSET_TABLE_TAB_OPTIONS = [
  { label: '테이블 기준', value: 'table' },
  { label: '컬럼 기준', value: 'column' },
] as SegmentedLabeledOption<AssetQualityTabValue>[];

const TITLE = '자산별 품질 상세';

const TABLE_SCROLL = { x: 1200, y: 538 } as const;
const EMPTY_TABLE_DESCRIPTION = '해당 조건에 일치하는 데이터가 없습니다';
const TABLE_LOCALE = {
  emptyText: <TableEmpty height={538} description={EMPTY_TABLE_DESCRIPTION} />,
};

export const DataAssetQuality = () => {
  const { selectedTabValue, handleTabChange } = useAssetQualityTabNavigation();
  const { type, columns, data, isLoading, paginationConfig } = useAssetQualityTable({ tabValue: selectedTabValue });

  const commonTableProps = useMemo(
    () => ({
      className: styles.table,
      pagination: paginationConfig,
      loading: isLoading,
      scroll: TABLE_SCROLL,
      locale: TABLE_LOCALE,
    }),
    [paginationConfig, isLoading]
  );

  const cardRef = useRef<HTMLDivElement>(null);

  const previousPath = useLocationHistoryStore((state) => state.previousPath);
  const isFromTablePage = previousPath?.includes('/table/') ?? false;

  useScrollIntoView({
    ref: cardRef,
    condition: isFromTablePage,
  });

  useEffect(() => {
    if (isFromTablePage) return;

    assetQualityFilterStoreActions.clearAllFilters('table');
    assetQualityFilterStoreActions.clearAllFilters('column');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={cardRef}>
      <CardHeader title={TITLE} />
      <Flex justify="start" className={styles.segmentedContainer}>
        <Segmented options={ASSET_TABLE_TAB_OPTIONS} value={selectedTabValue} onChange={handleTabChange} />
      </Flex>
      <AssetQualityFilter selectedAssetTableTabValue={selectedTabValue} />
      {type === 'column' ? (
        <Table<ColumnQualityTableData> {...commonTableProps} dataSource={data} columns={columns} />
      ) : (
        <Table<TableQualityTableData> {...commonTableProps} dataSource={data} columns={columns} />
      )}
    </div>
  );
};
